// src/services/cpf-external/jarvisly/adapter.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import entitiesService from 'services/entitiesService';

// *******************************************************************************
// ADAPTER INTERFACE
// *******************************************************************************

/**
 * Jarvisly CPF Provider Adapter (Database)
 * Searches for entity in internal database by CPF
 *
 * Implements provider contract:
 * - searchCpf(payload) → Promise<Object>
 * - normalizeRequestPayload(payload) → Object
 * - normalizeResponseBody(response) → Universal POJO
 */

/**
 * Searches CPF data in database
 * @param {Object} payload - Search parameters
 * @param {string} payload.cpf - CPF number (with or without formatting)
 * @returns {Promise<Object>} Universal POJO with CPF data
 */
async function searchCpf(payload) {
  const normalizedRequest = normalizeRequestPayload(payload);

  // Search entity by CPF in database
  const response = await entitiesService.getEntityByField(normalizedRequest.cpf);

  // Return raw response - factory will call normalizeResponseBody
  return response;
}

/**
 * Normalizes incoming payload to database query format
 * @param {Object} payload - Raw payload
 * @returns {Object} Database-compatible payload
 */
function normalizeRequestPayload(payload) {
  const { cpf } = payload;

  // Remove formatting from CPF (keep only numbers)
  const cleanCpf = String(cpf || '').replace(/\D/g, '');

  return {
    cpf: cleanCpf,
  };
}

/**
 * Normalizes database response to universal POJO
 * @param {Object} response - Database entity document or null
 * @returns {Object} Universal POJO
 *
 * Database response structure (entity document):
 * {
 *   _id: string,
 *   cpf: string,
 *   name: string,
 *   fullName: string,
 *   birthdate: string,
 *   email: string,
 *   phone: string,
 *   ...otherFields
 * }
 *
 * Universal POJO structure for CPF:
 * {
 *   provider: string,
 *   success: boolean,
 *   apiWorked: boolean,
 *   document: Object | null,  // Full entity document
 *   cpf: string,
 *   name: string,
 *   fullName: string,
 *   birthdate: string,
 *   email: string,
 *   phone: string,
 *   error: string | null,
 *   source: string
 * }
 */
function normalizeResponseBody(response) {
  const entityDoc = response;

  // Entity not found in database
  if (!entityDoc) {
    return {
      provider: 'jarvisly',
      success: false,
      apiWorked: true, // Database worked, but entity not found
      document: null,
      cpf: null,
      name: null,
      fullName: null,
      birthdate: null,
      email: null,
      phone: null,
      error: 'CPF não encontrado no banco de dados',
      source: 'jarvisly',
    };
  }

  // Success - Entity found in database
  return {
    provider: 'jarvisly',
    success: true,
    apiWorked: true,
    document: entityDoc, // Include full document for form filling
    cpf: entityDoc.cpf || null,
    name: entityDoc.name || null,
    fullName: entityDoc.fullName || null,
    birthdate: entityDoc.birthdate || null,
    email: entityDoc.email || null,
    phone: entityDoc.phone || null,
    error: null,
    source: 'jarvisly',
  };
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  searchCpf,
  normalizeRequestPayload,
  normalizeResponseBody,
};
